/**
 * Where «Назад» goes: the coach's history above the tabs.
 *
 * A tab is the floor. Opening a client (from the Calendar, the Clients
 * list, a lesson, another client) puts their card on top of whatever is
 * showing, and a lesson opened from the card goes on top of that. The tab
 * underneath stays as it was — its day, its view, its scroll — and «Назад»
 * takes the top layer off and puts the scroll back where it was.
 *
 * No React and no DOM here (CoachScreen.tsx runs it; scripts/verify/
 * nav-origin.ts tests it).
 */

export type Layer =
  | { kind: 'client'; clientId: string; name?: string }
  | { kind: 'lesson'; bookingId: string };

/** A layer, and the scroll of what it covers, to put back on «Назад». */
export type Entry = Layer & { under: number };

export interface Nav { tab: string; stack: Entry[] }

export const start = (tab: string): Nav => ({ tab, stack: [] });

/** Open something on top. `scrollY`: how far the screen it covers is scrolled. */
export function open(nav: Nav, layer: Layer, scrollY: number): Nav {
  return { tab: nav.tab, stack: [...nav.stack, { ...layer, under: scrollY }] };
}

/** «Назад»: the top layer comes off; `restore` is the scroll to put back. */
export function back(nav: Nav): { nav: Nav; restore: number | null } {
  if (nav.stack.length === 0) return { nav, restore: null };
  const top = nav.stack[nav.stack.length - 1];
  return { nav: { tab: nav.tab, stack: nav.stack.slice(0, -1) }, restore: top.under };
}

/** A tab tapped in the tab bar: that tab, nothing on top. */
export const toTab = (_nav: Nav, tab: string): Nav => start(tab);

export const top = (nav: Nav): Entry | null => nav.stack[nav.stack.length - 1] ?? null;

/**
 * What the card's own back link says: where «Назад» goes. Over the
 * Calendar, «‹ Календарь»; over the Clients list, «‹ Клиенты»; over
 * anything else (another client, Money), «‹ Назад».
 */
export function backTo(nav: Nav, index: number): 'calendar' | 'clients' | 'back' {
  if (index > 0) return 'back';
  return nav.tab === 'home' ? 'calendar' : nav.tab === 'clients' ? 'clients' : 'back';
}
